import mockComparison from '@data/mockComparison';
import mockComparisonMetrics from '@data/mockComparisonMetrics';
import type {
  PeerConsensusRatingApiRow,
  PeerMetricApiRow,
} from '@app-types/comparisonApi';
import type {
  ComparisonAverageScore,
  ComparisonCardData,
  ComparisonRatingBucket,
} from '@app-types/comparison';
import type {
  ComparisonMetricKey,
  MetricChartPoint,
  MetricComparisonCardData,
  MetricTableRow,
} from '@app-types/comparisonMetric';
import { PLACEHOLDER } from '@utils/format';
import { getTotalPages } from '@utils/pagination';

type Tone = 'up' | 'down' | 'neutral';

const METRIC_BASE_LABELS: Record<ComparisonMetricKey, string> = {
  salesRevenue: '売上高',
  ordinaryProfit: '経常利益',
  netIncome: '当期利益',
};

const METRIC_UNIT_LABELS: Record<string, string> = {
  K: '千',
  M: '百万',
  B: '十億',
  T: '兆',
};

const METRIC_CURRENCY_LABELS: Record<string, string> = {
  JPY: '円',
  USD: 'ドル',
};

type MetricPeriodKey =
  | 'twoYearsAgo'
  | 'oneYearAgo'
  | 'latest'
  | 'currentYearEstimate'
  | 'nextYearEstimate';

interface MetricPeriodSpec {
  key: MetricPeriodKey;
  valueField: keyof Pick<
    PeerMetricApiRow,
    | 'twoYearsAgoActual'
    | 'oneYearAgoActual'
    | 'latestActual'
    | 'currentYearEstimate'
    | 'nextYearEstimate'
  >;
  dateField: keyof Pick<
    PeerMetricApiRow,
    | 'twoYearsAgoDate'
    | 'oneYearAgoDate'
    | 'latestDate'
    | 'currentYearEstimateDate'
    | 'nextYearEstimateDate'
  >;
  fallbackLabel: string;
  forecast?: boolean;
}

const METRIC_PERIODS: readonly MetricPeriodSpec[] = [
  {
    key: 'twoYearsAgo',
    valueField: 'twoYearsAgoActual',
    dateField: 'twoYearsAgoDate',
    fallbackLabel: '2期前',
  },
  {
    key: 'oneYearAgo',
    valueField: 'oneYearAgoActual',
    dateField: 'oneYearAgoDate',
    fallbackLabel: '1期前',
  },
  {
    key: 'latest',
    valueField: 'latestActual',
    dateField: 'latestDate',
    fallbackLabel: '直近',
  },
  {
    key: 'currentYearEstimate',
    valueField: 'currentYearEstimate',
    dateField: 'currentYearEstimateDate',
    fallbackLabel: '今期予',
    forecast: true,
  },
  {
    key: 'nextYearEstimate',
    valueField: 'nextYearEstimate',
    dateField: 'nextYearEstimateDate',
    fallbackLabel: '来期予',
    forecast: true,
  },
];

const RATING_BUCKET_TEMPLATE: readonly Omit<ComparisonRatingBucket, 'value'>[] =
  mockComparison.cards[0]?.ratings.map(({ key, label, color }) => ({
    key,
    label,
    color,
  })) ?? [
    { key: 'veryBullish', label: '強気', color: '#e60000' },
    { key: 'bullish', label: 'やや強気', color: '#e28d8d' },
    { key: 'neutral', label: '中立', color: '#acacac' },
    { key: 'bearish', label: 'やや弱気', color: '#7d9eff' },
    { key: 'veryBearish', label: '弱気', color: '#3366ff' },
  ];

const toNumberOrNull = (value: number | string | null | undefined): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string') {
    const normalized = Number(value.replace(/,/g, '').trim());
    return Number.isFinite(normalized) ? normalized : null;
  }

  return null;
};

const toUsableQuoteNumber = (
  value: number | string | null | undefined,
): number | null => {
  const numeric = toNumberOrNull(value);
  return numeric === null || numeric === 0 ? null : numeric;
};

const toDisplayText = (value: string | null | undefined): string => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : PLACEHOLDER;
};

const normalizeMetadata = (value: string | null | undefined): string =>
  value?.trim().toUpperCase() ?? '';

/**
 * The backend's peer-row metadata describes all five actual/forecast values.
 * Missing or unknown metadata deliberately omits the unit instead of claiming
 * the historical mock `百万円` unit.
 */
const buildMetricValueHeader = (
  metricKey: ComparisonMetricKey,
  currency: string | null | undefined,
  unit: string | null | undefined,
): string => {
  const baseLabel = METRIC_BASE_LABELS[metricKey];
  const unitLabel = METRIC_UNIT_LABELS[normalizeMetadata(unit)];
  const currencyLabel = METRIC_CURRENCY_LABELS[normalizeMetadata(currency)];

  return unitLabel && currencyLabel
    ? `${baseLabel}(${unitLabel}${currencyLabel})`
    : baseLabel;
};

const getFractionDigits = (value: number, maxFractionDigits = 3): number => {
  if (!Number.isFinite(value)) return 0;
  const [, decimal = ''] = String(value).split('.');
  return Math.min(decimal.length, maxFractionDigits);
};

const formatFlexibleNumber = (
  value: number | string | null | undefined,
  maxFractionDigits = 3,
): string => {
  const numeric = toNumberOrNull(value);
  if (numeric === null) return PLACEHOLDER;

  return numeric.toLocaleString('ja-JP', {
    minimumFractionDigits: 0,
    maximumFractionDigits: getFractionDigits(numeric, maxFractionDigits),
  });
};

const formatSignedNumber = (
  value: number | string | null | undefined,
): string => {
  const numeric = toUsableQuoteNumber(value);
  if (numeric === null) return PLACEHOLDER;
  const formatted = formatFlexibleNumber(numeric);
  return numeric > 0 ? `+${formatted}` : formatted;
};

const formatSignedPercent = (
  value: number | string | null | undefined,
): string => {
  const numeric = toUsableQuoteNumber(value);
  if (numeric === null) return PLACEHOLDER;
  const sign = numeric > 0 ? '+' : '';
  return `${sign}${numeric.toLocaleString('ja-JP', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;
};

const formatChange = (
  netChange: number | string | null | undefined,
  pctChange: number | string | null | undefined,
): string => {
  const formattedNet = formatSignedNumber(netChange);
  const formattedPct = formatSignedPercent(pctChange);

  if (formattedNet === PLACEHOLDER && formattedPct === PLACEHOLDER) {
    return PLACEHOLDER;
  }

  return `${formattedNet} (${formattedPct})`;
};

const getChangeTone = (
  netChange: number | string | null | undefined,
  pctChange: number | string | null | undefined,
): Tone => {
  const preferred = toUsableQuoteNumber(netChange);
  const fallback = toUsableQuoteNumber(pctChange);
  const value = preferred ?? fallback;

  if (value === null) return 'neutral';
  return value > 0 ? 'up' : 'down';
};

const formatAverageScore = (value: number | string | null | undefined): string =>
  formatFlexibleNumber(value, 2);

const toRatingBucketValue = (value: number | string | null | undefined): number => {
  const numeric = toNumberOrNull(value);
  return numeric === null ? 0 : numeric;
};

const getRatingNodeId = (index: number): string =>
  mockComparison.cards[index % Math.max(mockComparison.cards.length, 1)]?.nodeId ??
  `comparison-rating-${index + 1}`;

const getMetricNodeId = (metricKey: ComparisonMetricKey, index: number): string => {
  const cards = mockComparisonMetrics[metricKey].cards;
  return cards[index % Math.max(cards.length, 1)]?.nodeId ??
    `comparison-${metricKey}-${index + 1}`;
};

const buildRatingBuckets = (
  peer: PeerConsensusRatingApiRow,
): ComparisonRatingBucket[] => {
  const values = [
    peer.strongBuy,
    peer.buy,
    peer.hold,
    peer.underPerform,
    peer.sell,
  ];

  return RATING_BUCKET_TEMPLATE.map((bucket, index) => ({
    ...bucket,
    value: toRatingBucketValue(values[index]),
  }));
};

const buildAverageScore = (
  peer: PeerConsensusRatingApiRow,
): ComparisonAverageScore => ({
  current: formatAverageScore(peer.meanRatingCurr),
  oneMonth: formatAverageScore(peer.meanRating1M),
  threeMonths: formatAverageScore(peer.meanRating3M),
  sixMonths: formatAverageScore(peer.meanRating6M),
  nineMonths: formatAverageScore(peer.meanRating9M),
  twelveMonths: formatAverageScore(peer.meanRating12M),
});

const formatPeriodDateLabel = (
  value: string | null | undefined,
  fallbackLabel: string,
  forecast: boolean,
): string => {
  const trimmed = value?.trim();
  if (!trimmed) return fallbackLabel;

  const match = trimmed.match(/^(\d{4})[/-](\d{1,2})/);
  if (!match) return fallbackLabel;

  const [, year, month] = match;
  const label = `${year}/${month.padStart(2, '0')}`;
  return forecast ? `${label}予` : label;
};

const buildMetricChartPoints = (peer: PeerMetricApiRow): MetricChartPoint[] =>
  METRIC_PERIODS.map((period) => {
    const value = toNumberOrNull(peer[period.valueField]) ?? 0;
    return {
      label: formatPeriodDateLabel(
        peer[period.dateField],
        period.fallbackLabel,
        Boolean(period.forecast),
      ),
      value,
      ...(period.forecast ? { forecast: true } : {}),
    };
  });

const buildMetricTableRows = (peer: PeerMetricApiRow): MetricTableRow[] =>
  METRIC_PERIODS.map((period) => ({
    fiscalYear: formatPeriodDateLabel(
      peer[period.dateField],
      period.fallbackLabel,
      Boolean(period.forecast),
    ),
    value: formatFlexibleNumber(peer[period.valueField], 3),
  }));

export const mapPeerConsensusRatingToCards = (
  peers: readonly PeerConsensusRatingApiRow[] | null | undefined,
): ComparisonCardData[] => (
  peers ?? []
).map((peer, index) => ({
  key: peer.ric?.trim() || peer.ticker?.trim() || `comparison-rating-${index + 1}`,
  nodeId: getRatingNodeId(index),
  code: toDisplayText(peer.ticker),
  name: toDisplayText(peer.nameJp),
  price: formatFlexibleNumber(toUsableQuoteNumber(peer.lastPrice)),
  change: formatChange(peer.netChange, peer.pctChange),
  changeTone: getChangeTone(peer.netChange, peer.pctChange),
  ratings: buildRatingBuckets(peer),
  averageScore: buildAverageScore(peer),
}));

export const mapPeerMetricToCards = (
  peers: readonly PeerMetricApiRow[] | null | undefined,
  metricKey: ComparisonMetricKey,
): MetricComparisonCardData[] => (
  peers ?? []
).map((peer, index) => ({
  key: `${metricKey}-${peer.ric?.trim() || peer.ticker?.trim() || index + 1}`,
  nodeId: getMetricNodeId(metricKey, index),
  code: toDisplayText(peer.ticker),
  name: toDisplayText(peer.nameJp),
  price: formatFlexibleNumber(toUsableQuoteNumber(peer.lastPrice)),
  change: formatChange(peer.netChange, peer.pctChange),
  changeTone: getChangeTone(peer.netChange, peer.pctChange),
  valueHeader: buildMetricValueHeader(metricKey, peer.currency, peer.unit),
  chartPoints: buildMetricChartPoints(peer),
  tableRows: buildMetricTableRows(peer),
}));

export const mapComparisonPagination = (
  total: number | null | undefined,
  pageSize: number,
): number => getTotalPages(total ?? 0, pageSize);
